import { Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import {
  crmPackageMappings,
  type CrmPackageMapping,
} from "../../api/crm-package-mappings.service.js";
import {
  toApiCreateRequest,
  type CrmPackageMappingFormData,
} from "../../schemas/crmPackageMappingSchema.js";
import { CrmPackageMappingForm } from "../CrmPackageMappingForm/index.js";
import styles from "./CrmPackageMappingCreateModal.module.css";

export type CrmPackageMappingCreateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (mapping: CrmPackageMapping) => void;
  onError?: (error: Error) => void;
  prefillCrmPackageId?: string;
};

export function CrmPackageMappingCreateModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  prefillCrmPackageId,
}: CrmPackageMappingCreateModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setServerError(null);
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement;

    const modal = modalRef.current;
    if (modal) {
      const focusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Tab" && modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    modal?.addEventListener("keydown", handleKeyDown);

    return () => {
      modal?.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [isOpen]);

  async function handleSubmit(formData: CrmPackageMappingFormData) {
    setIsSubmitting(true);
    setServerError(null);

    try {
      const apiRequest = toApiCreateRequest(formData);
      const createdMapping = await crmPackageMappings.create(apiRequest);

      setIsSubmitting(false);
      onSuccess(createdMapping);
    } catch (err) {
      setIsSubmitting(false);
      const error = err instanceof Error ? err : new Error("Failed to create mapping");
      setServerError(error.message);

      if (onError) {
        onError(error);
      }
    }
  }

  function handleCancel() {
    if (!isSubmitting) {
      onClose();
    }
  }

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose();
    }
  }

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-mapping-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="create-mapping-title" className={styles.modalTitle}>
            Create CRM Package Mapping
          </h2>
          <button
            className={styles.modalCloseButton}
            onClick={handleCancel}
            aria-label="Close modal"
            disabled={isSubmitting}
          >
            <Icon icon="cross" size="small" />
          </button>
        </div>

        <div className={styles.modalBody}>
          {serverError && (
            <div className={styles.serverError} role="alert" aria-live="assertive">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.errorIcon}
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
              <span>{serverError}</span>
            </div>
          )}

          <CrmPackageMappingForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Create Mapping"
            isSubmitting={isSubmitting}
            initialValues={prefillCrmPackageId ? { crmPackageId: prefillCrmPackageId } : undefined}
          />
        </div>
      </div>
    </div>
  );
}
